import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";
const title = "Management de transition";
const heroImage = "https://net-basis.net/Bilder/ict-interim-management-experts-dach-market-entry.webp";
const heroAlt = "Un homme d'affaires choisit des managers TIC pour diriger ventes et projets sur le marché DACH.";
const heroClaimLine1 = "Immédiat, flexible, expert, expérimenté ... temporaire !";
const benefitsTitle = "Comment vous profitez de notre expertise en __management de transition__ :";
const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

const widget2Text =
  "Votre partenaire expérimenté pour un Interim Management flexible lors de l'entrée sur le marché de la région DACH. Nous accompagnons les constructeurs de TIC étrangers sur le terrain selon leurs besoins et prenons en charge des domaines d'activité ciblés ou l'intégralité du projet international.";

const p1Text =
  "Diverses raisons peuvent rendre le thème du « Interim Management » important pour une entreprise lors de son entrée sur de nouveaux marchés. L'objectif stratégique d'étendre les activités à de nouveaux pays doit se faire indépendamment de l'exploitation existante. La mission doit être abordée de manière rapide, flexible et adaptée aux besoins, et elle est dès le départ définie pour une période limitée. L'adaptation à de nouveaux marchés et la recherche d'une position réussie sur le marché exigent des connaissances, des compétences et des expériences spécifiques que les propres collaborateurs ne peuvent ou ne doivent souvent pas remplir.";
const p2Text =
  "C'est précisément là que NetBasis International aide ses partenaires constructeurs étrangers à atteindre les objectifs internationaux souhaités ou précisément définis dans un laps de temps fixe. Ce faisant, nous ne prenons pas seulement en charge des domaines d'activité individuels (voir développement commercial), mais aussi, sur demande, l'intégralité du projet international.";
const p2LinkText = "développement commercial";
const p2LinkUrl = "/fr/services/developpement-commercial";

const benefits = [
  "Présence rapide sur place avec un effort de recrutement minimal",
  "Objectivité absolue pour votre projet international spécifique",
  "Solide expérience en gestion et compétence opérationnelle",
  "Connaissances approfondies du marché, compétences et contacts directs",
  "Travail ciblé et axé sur les résultats pour atteindre vos objectifs internationaux",
  "Relations contractuelles simples grâce à des délais de disponibilité et de résiliation courts",
  "Aucune concurrence ni conflit pour les cadres internes",
  "Effets précieux de développement du personnel selon le projet",
  "Visibilité absolue des coûts engagés et de notre rémunération",
];

export default function ManagementDeTransitionFrPage() {
  const strongName = COMPANY_NAME;
  const p1Parts = p1Text.split(strongName);
  const p2Parts = p2Text.split(strongName);

  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Management de Transition | NetBasis International"
        description="Compétence de direction temporaire expérimentée pour pallier les vacances ou gérer les phases critiques d’expansion."
        ogUrl="https://net-basis.net/fr/services/management-de-transition"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex flex-col items-start px-6 md:px-10 lg:pl-14 pointer-events-none mt-5 md:mt-10 lg:mt-10">
                <p
                  className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-left animate-[interimTextSlide_12s_ease-in-out_infinite]"
                  style={{
                    fontFamily: '"Open Sans", sans-serif',
                  }}
                >
                  {heroClaimLine1}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <h1 className="text-foreground-950 leading-relaxed text-sm md:text-base font-semibold mb-4">
              {widget2Text}
            </h1>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              <p>
                {p1Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p1Parts.length - 1 && (
                      <strong className="text-primary-600">{strongName}</strong>
                    )}
                  </span>
                ))}
              </p>
              <p>
                {(() => {
                  const parts = p2Text.split(strongName);
                  return parts.map((part, i) => {
                    const linkIdx = part.indexOf(p2LinkText);
                    if (linkIdx !== -1) {
                      const before = part.slice(0, linkIdx);
                      const after = part.slice(linkIdx + p2LinkText.length);
                      return (
                        <span key={i}>
                          {before}
                          <a href={p2LinkUrl} className="text-primary-600 hover:underline">{p2LinkText}</a>
                          {after}
                        </span>
                      );
                    }
                    return (
                      <span key={i}>
                        {part}
                        {i < parts.length - 1 && <strong className="text-primary-600">{strongName}</strong>}
                      </span>
                    );
                  });
                })()}
              </p>
            </div>

            {/* Benefits heading with italic word */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i}>{part}</em> : part
              )}
            </h2>

            {/* Benefits with checkmarks */}
            <div className="space-y-1.5 md:space-y-2 text-foreground-950 leading-relaxed text-sm md:text-base">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 md:gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-600 text-base md:text-lg"></i>
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}